import React, { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { apiClient, wsUrl } from "@/lib/api";
import { BRANCHES, formatIST, maskPhone } from "@/lib/branches";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Copy, Check } from "lucide-react";

function fireConfetti() {
  const colors = ["#CC0000", "#FFFFFF", "#E6B800"];
  confetti({
    particleCount: 140, spread: 80, origin: { y: 0.6 }, colors,
  });
  setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { y: 0.7 }, colors }), 250);
}

export default function DrawTab() {
  const [summary, setSummary] = useState(null);
  const [winnersToday, setWinnersToday] = useState([]);
  const [branch, setBranch] = useState("");
  const [rolling, setRolling] = useState(false);
  const [rollNames, setRollNames] = useState([]);
  const [revealed, setRevealed] = useState(null);
  const [redrawConfirmOpen, setRedrawConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wsRef = useRef(null);

  const loadAll = async () => {
    try {
      const [s, w] = await Promise.all([
        apiClient.get("/summary"),
        apiClient.get("/winners"),
      ]);
      setSummary(s.data);
      setWinnersToday(w.data.winners || []);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    loadAll();
    try {
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onmessage = () => loadAll();
    } catch (_e) { /* noop */ }
    return () => { try { wsRef.current?.close(); } catch (_e) { /* noop */ } };
  }, []);

  const branchEntries = useMemo(() => {
    if (!summary || !branch) return 0;
    const row = summary.rows.find(r => r.branch === branch);
    return row?.entries || 0;
  }, [summary, branch]);

  const existingWinner = useMemo(() => {
    if (!summary || !branch) return null;
    const row = summary.rows.find(r => r.branch === branch);
    return row?.winner || null;
  }, [summary, branch]);

  const startDraw = async (forceRedraw = false) => {
    if (!branch) { toast.error("Select a branch"); return; }
    if (branchEntries === 0) { toast.error("No entries for this branch today"); return; }
    setRevealed(null);
    setCopied(false);

    // Get pool to display rolling names
    let pool = [];
    try {
      const { data } = await apiClient.get("/registrations", { params: { branch } });
      pool = data.registrations.map(r => r.name);
    } catch (_e) { /* noop */ }
    if (pool.length === 0) pool = ["..."];

    // Build long rolling list
    const lines = [];
    for (let i = 0; i < 40; i++) {
      lines.push(pool[Math.floor(Math.random() * pool.length)]);
    }
    setRollNames(lines);
    setRolling(true);

    // Fire the draw API in parallel with animation
    let winner = null;
    try {
      const { data } = await apiClient.post("/draw", { branch, redraw: forceRedraw });
      winner = data.winner;
    } catch (err) {
      setRolling(false);
      toast.error(err?.response?.data?.detail || "Draw failed");
      return;
    }

    // After ~2.5s, stop and reveal
    setTimeout(() => {
      setRolling(false);
      setRevealed(winner);
      fireConfetti();
      loadAll();
    }, 2500);
  };

  const handleDrawClick = () => {
    if (existingWinner) {
      setRedrawConfirmOpen(true);
    } else {
      startDraw(false);
    }
  };

  const announcement = revealed
    ? `🏆 ഇനത്തെ Lucky Draw Winner!
Today's Lucky Draw Winner!
Branch: ${revealed.branch}
Winner: ${revealed.name}
Congratulations! 🎉 Please visit the branch to claim your prize.
— Konattu Group`
    : "";

  const copyAnnouncement = async () => {
    try {
      await navigator.clipboard.writeText(announcement);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="space-y-8" data-testid="draw-tab">
      {/* Status strip */}
      {summary && (
        <div className="kg-card p-4 flex flex-wrap gap-x-6 gap-y-2 items-center" data-testid="draw-status-strip">
          <span className="text-sm font-semibold text-neutral-800">
            {summary.drawn_count} of {summary.total_branches} branches have drawn winners today
          </span>
          <span className="text-sm">·</span>
          <span className="text-sm font-semibold" style={{ color: summary.zero_entry_branches.length > 0 ? "#CC0000" : "#16a34a" }}>
            {summary.zero_entry_branches.length} branches have 0 entries
          </span>
          {summary.zero_entry_branches.length > 0 && (
            <div className="w-full text-xs text-red-600 mt-1">
              Zero-entry: {summary.zero_entry_branches.join(", ")}
            </div>
          )}
        </div>
      )}

      <div className="kg-card kg-card-red-top p-6">
        <h3 className="font-display text-2xl text-neutral-900">Draw Winner</h3>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="text-sm text-neutral-700">Select branch</label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="kg-input mt-2 h-12 border-2" data-testid="draw-branch-select">
                <SelectValue placeholder="Choose branch" />
              </SelectTrigger>
              <SelectContent>
                {(summary?.rows || BRANCHES.map(b => ({ branch: b, entries: 0, winner: null }))).map((r, i) => (
                  <SelectItem key={i} value={r.branch} data-testid={`draw-branch-opt-${i}`}>
                    {r.winner ? "✓ " : ""}{r.branch} ({r.entries} entries)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleDrawClick}
            disabled={!branch || branchEntries === 0 || rolling}
            className="kg-btn-red h-14 px-8 text-base"
            data-testid="draw-winner-btn"
          >
            🎰 {existingWinner ? "Re-draw" : "Draw Winner"}
          </Button>
        </div>

        {branch && branchEntries === 0 && (
          <p className="mt-3 text-sm text-red-600" data-testid="no-entries-msg">
            No entries yet for this branch today.
          </p>
        )}

        {existingWinner && !revealed && (
          <div className="mt-4 p-4 rounded-xl bg-neutral-50 border border-neutral-200">
            <p className="text-xs uppercase tracking-widest text-neutral-500">Already drawn</p>
            <p className="font-display text-2xl mt-1">{existingWinner.name}</p>
            <p className="text-sm text-neutral-600">{maskPhone(existingWinner.phone)} · {formatIST(existingWinner.drawn_at)}</p>
          </div>
        )}

        {rolling && (
          <div className="mt-6">
            <div className="slot-window" data-testid="slot-machine">
              <div className="slot-track">
                {rollNames.map((n, i) => (
                  <div key={i} className="slot-line">{n}</div>
                ))}
              </div>
            </div>
            <p className="text-center text-sm text-neutral-500 mt-2">Drawing the winner…</p>
          </div>
        )}

        {revealed && (
          <div className="mt-6 kg-winner-card p-6 fade-up" data-testid="winner-reveal">
            <p className="text-xs uppercase tracking-widest font-semibold" style={{color:"#CC0000"}}>{revealed.branch}</p>
            <h3 className="font-display text-4xl text-neutral-900 mt-2">🎉 {revealed.name}</h3>
            <p className="text-neutral-700 mt-2">{maskPhone(revealed.phone)} · Drawn at {formatIST(revealed.drawn_at)}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={copyAnnouncement} className="kg-btn-red h-11" data-testid="copy-announcement-btn">
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copied ✓</> : <><Copy className="w-4 h-4 mr-2" /> 📋 Copy Announcement</>}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setRevealed(null); }}
                data-testid="close-reveal-btn"
              >Close</Button>
            </div>
            <pre className="mt-4 p-3 bg-neutral-50 rounded-lg text-xs text-neutral-700 whitespace-pre-wrap font-mono">
{announcement}
            </pre>
          </div>
        )}
      </div>

      {/* All winners today */}
      <div className="kg-card p-6" data-testid="all-winners-today">
        <h3 className="font-display text-xl text-neutral-900">All Winners Today ({winnersToday.length})</h3>
        {winnersToday.length === 0 ? (
          <p className="text-neutral-500 mt-3 text-sm">No winners drawn yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 pr-4">Branch</th>
                  <th className="py-2 pr-4">Winner</th>
                  <th className="py-2 pr-4">Phone</th>
                  <th className="py-2 pr-4">Drawn</th>
                  <th className="py-2 pr-4 w-16 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {winnersToday.map((w) => (
                  <tr key={w.id} className="border-b border-neutral-100">
                    <td className="py-2 pr-4">{w.branch}</td>
                    <td className="py-2 pr-4 font-semibold">{w.name}</td>
                    <td className="py-2 pr-4 font-mono">{maskPhone(w.phone)}</td>
                    <td className="py-2 pr-4 text-neutral-500">{formatIST(w.drawn_at)}</td>
                    <td className="py-2 pr-4 text-right">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(`Delete winner ${w.name} for ${w.branch}?`)) return;
                          try {
                            await apiClient.delete(`/winners/${w.id}`, { params: { pin: "1234" } });
                            toast.success("Winner removed");
                            loadAll();
                          } catch { toast.error("Delete failed"); }
                        }}
                        className="text-[#CC0000] hover:text-[#990000] text-sm font-semibold"
                        data-testid={`admin-delete-winner-${w.id}`}
                      >Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={redrawConfirmOpen} onOpenChange={setRedrawConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-draw winner?</AlertDialogTitle>
            <AlertDialogDescription>
              A winner has already been drawn for <b>{branch}</b>. Re-drawing will overwrite the existing winner. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="redraw-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => startDraw(true)} data-testid="redraw-confirm" className="bg-[#CC0000] hover:bg-[#990000]">
              Yes, Re-draw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
